// Per-LP-epoch performance history for a strategy.
//
// An "epoch" is the lifetime of one LP NFT — from mint to close.
// Every redeploy creates a new epoch row in the DB; the deployer
// records mint amounts, the redeploy flow records close amounts +
// fees + AERO. This table renders that history so the operator can
// see "epoch #N earned X in fees + Y in AERO − Z in IL = net P&L W"
// for every position the strategy has ever held.
//
// Open epochs (status='open') show "—" in close-related columns and
// "live" in duration. Their P&L columns are mark-to-market — the
// dashboard API substitutes the current LP value + pending fees +
// pending AERO for the not-yet-realized close amount.
//
// Two P&L columns, split intentionally:
//
//   LP P&L       Pure LP-side return: close + fees + AERO − mint − gas.
//                Tells you whether the LP itself made money, ignoring
//                the hedge. Useful for comparing range/pool choices.
//
//   Strategy P&L LP P&L + HL funding + HL realized PnL. The honest
//                delta-neutral cycle return. An LP can show fat fees
//                while the hedge bled funding; only the strategy
//                number captures that.
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useEpochs } from '../hooks/useApi';
import type { LpEpochRow } from '../lib/api';
import { SectionHeader } from './ui/section-header';
import { StatBadge } from './ui/stat-badge';
import { cn, formatCurrency, formatDuration } from '../lib/utils';

interface Props {
  strategyId: number | null;
}

const PAGE_SIZE = 10;

export function LpEpochsTable({ strategyId }: Props) {
  const [page, setPage] = useState(0);
  // Fetch one extra row beyond the page so we know whether a next page
  // exists without a separate count query.
  const { data: rows, isLoading } = useEpochs(
    strategyId,
    PAGE_SIZE + 1,
    page * PAGE_SIZE,
  );
  const hasNext = (rows?.length ?? 0) > PAGE_SIZE;
  const epochs = rows?.slice(0, PAGE_SIZE);
  // Currently-open epoch (if any) — surfaced as a one-line summary
  // above the table so the operator can see the LP movement at a
  // glance without parsing the full row. Newest-first ordering means
  // it only appears on page 0, which is also where the summary makes
  // sense.
  const openEpoch = (epochs ?? []).find((e) => e.status === 'open');

  return (
    <div className="rounded-lg border border-border bg-surface p-6 flex flex-col">
      <SectionHeader
        title="LP positions"
        subtitle="Mint → close history. Each row = one LP NFT lifecycle. Open epochs show live mark-to-market value + P&L."
        size="sm"
        className="mb-4"
      />
      {openEpoch && openEpoch.currentLpValueUsd !== null && (
        <OpenEpochSummary
          mintTotalUsd={openEpoch.mintTotalUsd}
          currentLpValueUsd={openEpoch.currentLpValueUsd}
        />
      )}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !epochs || epochs.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No LP epochs yet. They'll appear after the first mint via the new
          deployment flow.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide">
                <th className="font-medium pb-2 pr-4">NFT</th>
                <th className="font-medium pb-2 pr-4">Status</th>
                <th className="font-medium pb-2 pr-4">Range</th>
                <th className="font-medium pb-2 pr-4">Duration</th>
                <th className="font-medium pb-2 pr-4 text-right">Mint</th>
                <th
                  className="font-medium pb-2 pr-4 text-right"
                  title="Live LP value (lp_eth × eth_price + lp_usdc) for open epochs. For closed epochs, the close_total_usd recorded at decreaseLiquidity time."
                >
                  Live / Close
                </th>
                <th className="font-medium pb-2 pr-4 text-right">Fees</th>
                <th className="font-medium pb-2 pr-4 text-right">AERO</th>
                <th className="font-medium pb-2 pr-4 text-right">Gas</th>
                <th
                  className="font-medium pb-2 pr-4 text-right"
                  title="LP-only return: close + fees + AERO − mint − gas. Ignores the hedge."
                >
                  LP P&amp;L
                </th>
                <th
                  className="font-medium pb-2 pr-4 text-right"
                  title="HL funding accrued during the epoch (signed). Negative when the strategy paid to keep the short open."
                >
                  HL Fund
                </th>
                <th
                  className="font-medium pb-2 pr-4 text-right"
                  title="Realized PnL from hedge resizes during the epoch."
                >
                  HL Real
                </th>
                <th
                  className="font-medium pb-2 pr-4 text-right"
                  title="HL unrealized PnL on the open perp position (live MTM). Offsets LP IL when the hedge is sized correctly."
                >
                  HL Unreal
                </th>
                <th
                  className="font-medium pb-2 pr-4 text-right"
                  title="Total HL trading fees paid during the epoch (taker fee × notional, summed across all auto_hedge actions)."
                >
                  HL Fees
                </th>
                <th
                  className="font-medium pb-2 text-right"
                  title="Full delta-neutral cycle return: LP P&L + HL funding + HL realized + HL unrealized − HL fees. Gas is already inside LP P&L."
                >
                  Strategy P&amp;L
                </th>
              </tr>
            </thead>
            <tbody>
              {epochs.map((e) => (
                <EpochRow key={e.id} e={e} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(page > 0 || hasNext) && (
        <div className="flex items-center justify-end gap-3 pt-3 text-xs">
          <button
            className="px-2 py-1 rounded border border-border disabled:opacity-40 hover:bg-muted"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ← Newer
          </button>
          <span className="text-muted-foreground tabular-nums">
            page {page + 1}
          </span>
          <button
            className="px-2 py-1 rounded border border-border disabled:opacity-40 hover:bg-muted"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
          >
            Older →
          </button>
        </div>
      )}
    </div>
  );
}

function EpochRow({ e }: { e: LpEpochRow }) {
  const isOpen = e.status === 'open';
  const durationMs =
    e.durationSec !== null
      ? e.durationSec * 1000
      : isOpen
        ? Date.now() - e.mintTs
        : null;

  // Prefer the new lpPnlUsd / strategyPnlUsd from the API. Fall back to
  // legacy netPnlUsd for older snapshots (closed epochs that pre-date
  // migration 0014 have netPnlUsd populated but the new fields null).
  const lpPnl = e.lpPnlUsd ?? e.netPnlUsd;
  const strategyPnl = e.strategyPnlUsd;

  return (
    <tr className="border-t border-border">
      <td className="py-2 pr-4 text-xs font-mono">
        <a
          href={`https://basescan.org/token/0x827922686190790b37229fd06084350E74485b72?a=${e.nftId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline inline-flex items-center gap-1"
          title={`NFT #${e.nftId}`}
        >
          #{e.nftId.slice(-6)}
          <ExternalLink className="h-3 w-3" />
        </a>
      </td>
      <td className="py-2 pr-4">
        <StatBadge
          label={isOpen ? 'Open' : 'Closed'}
          variant={isOpen ? 'positive' : 'neutral'}
          dot
        />
      </td>
      <td className="py-2 pr-4 text-xs tabular-nums">±{e.mintRangePct}%</td>
      <td className="py-2 pr-4 text-xs tabular-nums text-muted-foreground">
        {durationMs !== null ? formatDuration(durationMs) : '—'}
        {isOpen && <span className="ml-1 text-positive">live</span>}
      </td>
      <td className="py-2 pr-4 tabular-nums text-right text-xs">
        {formatCurrency(e.mintTotalUsd)}
      </td>
      <td className="py-2 pr-4 tabular-nums text-right text-xs">
        {/* Closed epochs: show stored close value. Open epochs: live
            mark-to-market from latest snapshot. Italic for the live one
            so it's visually distinguishable from a finalized value. */}
        {e.closeTotalUsd !== null ? (
          formatCurrency(e.closeTotalUsd)
        ) : e.currentLpValueUsd !== null ? (
          <span className="italic">{formatCurrency(e.currentLpValueUsd)}</span>
        ) : (
          '—'
        )}
      </td>
      <td className="py-2 pr-4 tabular-nums text-right text-xs text-positive">
        {e.feesCollectedUsd > 0 ? `+${formatCurrency(e.feesCollectedUsd)}` : '—'}
      </td>
      <td className="py-2 pr-4 tabular-nums text-right text-xs text-positive">
        {e.aeroClaimedUsd > 0 ? `+${formatCurrency(e.aeroClaimedUsd)}` : '—'}
      </td>
      <td className="py-2 pr-4 tabular-nums text-right text-xs text-muted-foreground">
        {e.gasTotalUsd > 0 ? `−${formatCurrency(e.gasTotalUsd)}` : '—'}
      </td>
      <SignedCell value={lpPnl} />
      <SignedCell value={e.hlFundingUsd} dim />
      <SignedCell value={e.hlRealizedPnlUsd} dim />
      <SignedCell value={e.hlUnrealizedPnlUsd} dim />
      {/* HL Fees displayed as negative cost (the sign convention for
          fees is "amount paid" — always a positive number — but to make
          the column visually consistent with other costs we render with
          a leading minus sign and muted-foreground color when nonzero. */}
      <td className="py-2 pr-4 tabular-nums text-right text-xs text-muted-foreground">
        {e.hlFeesUsd !== null && e.hlFeesUsd > 0
          ? `−${formatCurrency(e.hlFeesUsd)}`
          : '—'}
      </td>
      <SignedCell value={strategyPnl} bold />
    </tr>
  );
}

/**
 * One-line summary above the table for the currently-open epoch. Shows
 * the LP value trajectory (mint → live) at a glance so the operator
 * doesn't have to mentally diff Mint and Live columns. P&L sign uses
 * positive/negative coloring; "flat" stays neutral.
 */
function OpenEpochSummary({
  mintTotalUsd,
  currentLpValueUsd,
}: {
  mintTotalUsd: number;
  currentLpValueUsd: number;
}) {
  const delta = currentLpValueUsd - mintTotalUsd;
  const trend: 'positive' | 'negative' | 'neutral' =
    delta > 0.0049 ? 'positive' : delta < -0.0049 ? 'negative' : 'neutral';
  const pct = mintTotalUsd > 0 ? (delta / mintTotalUsd) * 100 : 0;
  return (
    <div className="mb-4 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs">
      <span className="uppercase tracking-wide text-muted-foreground">
        Open LP value
      </span>
      <span className="font-medium tabular-nums">
        {formatCurrency(mintTotalUsd)}
      </span>
      <span className="text-muted-foreground">→</span>
      <span className="font-semibold tabular-nums text-foreground">
        {formatCurrency(currentLpValueUsd)}
      </span>
      <span
        className={cn(
          'inline-flex items-center gap-1 tabular-nums font-medium',
          trend === 'positive' && 'text-positive',
          trend === 'negative' && 'text-negative',
          trend === 'neutral' && 'text-muted-foreground',
        )}
      >
        ({delta >= 0 ? '+' : ''}
        {formatCurrency(delta)} · {delta >= 0 ? '+' : ''}
        {pct.toFixed(2)}%)
      </span>
      <span className="text-muted-foreground">
        — LP token MTM only; fees + AERO + HL excluded
      </span>
    </div>
  );
}

function SignedCell({
  value,
  bold = false,
  dim = false,
}: {
  value: number | null;
  bold?: boolean;
  dim?: boolean;
}) {
  const trend: 'positive' | 'negative' | 'neutral' =
    value === null
      ? 'neutral'
      : value > 0
        ? 'positive'
        : value < 0
          ? 'negative'
          : 'neutral';
  return (
    <td
      className={cn(
        'py-2 pr-4 tabular-nums text-right text-xs',
        bold && 'font-medium',
        dim && trend === 'neutral' && 'text-muted-foreground',
        !dim && trend === 'neutral' && 'text-muted-foreground',
        trend === 'positive' && 'text-positive',
        trend === 'negative' && 'text-negative',
      )}
    >
      {value !== null ? formatCurrency(value) : '—'}
    </td>
  );
}
